import supabase from '../config/supabase';
import { isMissingParNormalizationColumnError } from '../utils/countUnitModel';

// The vendor-neutral PAR columns (migration 20260822120000 on
// count_template_items, 20260730120000 on count_items). The TEMPLATE is what
// seeds the next count, so propagating only par_level/par_unit meant a PAR
// resolved as "1 Vendor A case = 12 bottles" came back next session as the
// bare pair (1, 'case') — a wrapper word with no package, which every
// resolver correctly refuses to read as a quantity. The item then drops out
// of ordering, or reads as 1 where the kitchen meant 12.
const PAR_NORMALIZATION_FIELDS = [
  'par_normalized_quantity',
  'par_normalized_unit',
  'par_display_quantity',
  'par_display_unit',
  'par_purchase_option_id',
];

/** Only the normalization keys a caller actually supplied, as a patch. */
const parNormalizationPatchFrom = (source) => {
  if (!source || typeof source !== 'object') return null;
  const patch = {};
  PAR_NORMALIZATION_FIELDS.forEach((field) => {
    if (field in source) patch[field] = source[field] ?? null;
  });
  return Object.keys(patch).length > 0 ? patch : null;
};

/**
 * Writes a PAR patch to count_template_items, degrading to the legacy pair on
 * a store whose template table predates the normalization columns — the same
 * contract every other PAR writer uses (isMissingParNormalizationColumnError).
 *
 * @param {Function} buildQuery  (patch) => a filtered supabase update builder
 * @param {Object} fullPatch     legacy pair + normalization quintet
 * @param {Object} legacyPatch   legacy pair alone
 */
const writeTemplatePar = async (buildQuery, fullPatch, legacyPatch) => {
  const first = await buildQuery(fullPatch);
  if (first.error && fullPatch !== legacyPatch
    && isMissingParNormalizationColumnError(first.error)) {
    return buildQuery(legacyPatch);
  }
  return first;
};

/**
 * Propagate per-count PAR edits back to the parent template's PAR
 * (count_template_items.par_level / par_unit), so the next count created from
 * the same template pre-fills the user's freshly entered value "as if
 * it had been set in the template editor".
 *
 * Storage layout (single source of truth):
 *   - PAR is per-template per-item, stored on count_template_items.
 *     One row exists per (template_id, inventory_item_id, location);
 *     PAR is shared across locations, so we update every matching row.
 *   - MOQ is NOT a counting fact. The real minimum lives on the vendor's
 *     purchase option (purchase_options.moq_packages) and is applied in
 *     Savings Review. `min_order_qty` is still ACCEPTED here and still
 *     written to the legacy inventory_items.moq / moq_unit columns, for the
 *     non-counting callers that edit the item record directly — but it is
 *     never mirrored onto count_items any more, so counting can no longer
 *     manufacture an item-level minimum. No counting surface passes it.
 *   - The per-count snapshot (count_items.par_level) is written by the caller
 *     before invoking this helper. We only handle the parent propagation here.
 *
 * Best-effort: failures are logged but never thrown — the user's
 * in-session edit has already been persisted by the time we run.
 *
 * PAR propagation logic (in priority order):
 *   0. If the session has no template_id, re-resolve it via two passes
 *      and persist the resolved id back onto count_sessions:
 *        a. count_group_id sibling lookup — if any sibling session in
 *           the same count_group already has a template_id, adopt it
 *           (newest if siblings disagree). This is the strongest signal.
 *        b. inventory-overlap heuristic — sample count_items, find the
 *           count_template_items template with the most overlapping
 *           unique inventory_item_ids. Confident when ratio ≥ 0.5 AND
 *           the winner strictly beats the runner-up (ties only allowed
 *           when ratio ≥ 0.8, broken toward the largest template_id).
 *           First try is store-scoped; falls back to no-store-id query
 *           when older cti rows have null store_id.
 *   1. If templateId is set and a matching count_template_items row exists
 *      → update count_template_items.par_level (the normal path).
 *   2. If templateId is set but UPDATE matched 0 rows: first try a by-PK
 *      fallback using the same (template_id, inventory_item_id) filter.
 *      If that also returns 0 rows, try resolving the canonical
 *      inventory_item_id via count_items to handle id-shape mismatches
 *      (e.g. caller passed a count_items.id instead of the inventory FK).
 *      If a match is found, update those rows. Only if no template row
 *      exists at all → upsert a new row so PAR sticks on future counts
 *      (reason: 'template-row-inserted'). If even that fails, fall back
 *      to writing inventory_items.par_level (reason: 'no-template-row').
 *   3. If templateId is null after re-resolution (true ad-hoc / EOP
 *      session) → write inventory_items.par_level directly so the value
 *      survives (reason: 'no-template-id'). MOQ behavior is unchanged.
 *   4. On every successful count_template_items update (the normal path,
 *      no fallback) we also mirror PAR to inventory_items.par_level so
 *      the GroupEditor's null/0 fallback path never shows stale data.
 */

// Best-effort browser notification: tell any open template-editor view that
// the in-progress count just wrote to (or inserted into) the sibling
// is_template=true count_session's count_items, so it can refetch its item
// list without waiting for a hard refresh. Listened for in public/count.html
// (search for `templateSessionItemsUpdated`). No-op outside the browser.
const notifyTemplateSessionItemsChanged = (detail) => {
  try {
    if (typeof window === 'undefined') return;
    if (typeof CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent('templateSessionItemsUpdated', { detail }));
  } catch (_) {
    // Notification is best-effort; visibility/focus refetch will recover.
  }
};

// Coerce a user-entered value (which may be a number, a numeric string from
// an <input>, an empty string, or null) into either a finite number, null
// (meaning "clear"), or undefined (meaning "don't touch this field").
const coerceNumericField = (value) => {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  const trimmed = String(value).trim();
  if (trimmed === '') return null;
  const parsed = Number.parseFloat(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

// Insert (upsert) brand-new count_template_items rows for an item that the
// user added to an in-progress count. The template row is the source of
// truth for PAR on future counts, so creating it here makes the user's PAR
// edit "stick" the next time the template is used to start a count.
//
// We add one row per distinct location the item appears at in the current
// count session, mirroring how the template editor seeds rows. The unique
// constraint UNIQUE(template_id, inventory_item_id, location) is used as
// the conflict target so we don't double-insert if anything raced us.
const upsertNewTemplateRowsForItem = async ({
  templateId,
  inventoryItemId,
  storeId,
  sessionId,
  parValue,
  par_unit,
  parDefaulted = false,
  parNormalization = null,
}) => {
  if (!templateId || !inventoryItemId || !sessionId) return false;

  // 1) Discover which location(s) the item is being counted in. PAR is per
  //    (template, item, location), so we want a template row for each.
  let locations = [];
  try {
    const { data: countRows, error: ciErr } = await supabase
      .from('count_items')
      .select('location')
      .eq('count_session_id', sessionId)
      .eq('inventory_item_id', inventoryItemId);
    if (ciErr) {
      console.warn(
        '[propagateGlobalCountFieldsToParents] template upsert: count_items lookup failed:',
        ciErr
      );
      return false;
    }
    const seen = new Set();
    (countRows || []).forEach((row) => {
      // Mirror the template editor's default of 'No Location' so the unique
      // constraint and the editor stay in agreement (NULLs are not equal in
      // a SQL UNIQUE constraint and would defeat the conflict target).
      const loc = row.location && String(row.location).trim() !== ''
        ? row.location
        : 'No Location';
      if (!seen.has(loc)) {
        seen.add(loc);
        locations.push(loc);
      }
    });
  } catch (err) {
    console.warn(
      '[propagateGlobalCountFieldsToParents] template upsert: count_items lookup threw:',
      err
    );
    return false;
  }
  if (locations.length === 0) return false;

  // 2) Compute next sort position per (template, location) to satisfy the
  //    UNIQUE(template_id, location, template_sort_position) constraint.
  const nextSortByLocation = new Map();
  try {
    const { data: existingRows, error: posErr } = await supabase
      .from('count_template_items')
      .select('location, template_sort_position')
      .eq('template_id', templateId);
    if (posErr) {
      console.warn(
        '[propagateGlobalCountFieldsToParents] template upsert: sort-position lookup failed:',
        posErr
      );
      return false;
    }
    const maxByLocation = new Map();
    (existingRows || []).forEach((row) => {
      const loc = row.location && String(row.location).trim() !== ''
        ? row.location
        : 'No Location';
      const pos = Number(row.template_sort_position) || 0;
      if (!maxByLocation.has(loc) || pos > maxByLocation.get(loc)) {
        maxByLocation.set(loc, pos);
      }
    });
    locations.forEach((loc) => {
      const cur = maxByLocation.get(loc) ?? 0;
      nextSortByLocation.set(loc, cur + 10);
    });
  } catch (err) {
    console.warn(
      '[propagateGlobalCountFieldsToParents] template upsert: sort-position lookup threw:',
      err
    );
    return false;
  }

  // 3) Build and upsert the new rows.
  const rows = locations.map((loc) => {
    const row = {
      template_id: templateId,
      inventory_item_id: inventoryItemId,
      location: loc,
      template_sort_position: nextSortByLocation.get(loc),
      include_in_count: true,
      par_level: parValue,
      par_defaulted: parDefaulted === true,
    };
    if (par_unit) row.par_unit = par_unit;
    if (storeId) row.store_id = storeId;
    return row;
  });
  const legacyRows = rows;
  const fullRows = parNormalization
    ? rows.map((row) => ({ ...row, ...parNormalization }))
    : rows;

  try {
    const upsert = (body) => supabase
      .from('count_template_items')
      .upsert(body, { onConflict: 'template_id,inventory_item_id,location' });
    let { error: upErr } = await upsert(fullRows);
    // A store behind on migration 20260822120000 has no normalization columns
    // on its template table; the PAR still has to land.
    if (upErr && parNormalization && isMissingParNormalizationColumnError(upErr)) {
      ({ error: upErr } = await upsert(legacyRows));
    }
    if (upErr) {
      console.warn(
        '[propagateGlobalCountFieldsToParents] template upsert failed:',
        upErr
      );
      return false;
    }
    return true;
  } catch (err) {
    console.warn(
      '[propagateGlobalCountFieldsToParents] template upsert threw:',
      err
    );
    return false;
  }
};

// Write PAR to inventory_items.par_level as a fallback when no template row
// can receive the update. Gated behind an explicit reason so it cannot fire
// by accident on the normal (templateId + matching row) path.
//
// NOTE: inventory_items has a par_level column but NO par_unit column — PAR
// units are template-scoped (count_template_items.par_unit). Writing par_unit
// here returns PGRST204 ("Could not find the 'par_unit' column") and 400s the
// whole request, which previously made PAR propagation report failure.
const fallbackParToInventoryItem = async (
  inventoryItemId,
  storeId,
  parValue,
  reason,
  parDefaulted = false,
) => {
  if (!storeId) {
    console.warn(
      `[propagateGlobalCountFieldsToParents] PAR fallback (${reason}) skipped: session has no store_id. ` +
      `inventoryItemId=${inventoryItemId}`
    );
    return false;
  }
  const patch = { par_level: parValue, par_defaulted: parDefaulted === true };
  try {
    const { error } = await supabase
      .from('inventory_items')
      .update(patch)
      .eq('id', inventoryItemId)
      .eq('store_id', storeId);
    if (error) {
      console.warn(
        `[propagateGlobalCountFieldsToParents] PAR fallback (${reason}) -> inventory_items failed:`,
        error
      );
      return false;
    }
    return true;
  } catch (err) {
    console.warn(
      `[propagateGlobalCountFieldsToParents] PAR fallback (${reason}) threw:`,
      err
    );
    return false;
  }
};

export async function propagateGlobalCountFieldsToParents({
  sessionId,
  inventoryItemId,
  par_level,
  par_unit,
  // True only while a one-unit PAR came from the new-item creation default.
  // Every normal editor passes/assumes false, including a human confirming 1.
  par_defaulted,
  // The vendor-neutral PAR the count page already computed for count_items
  // (buildParNormalizationPatch). Passed through so the TEMPLATE stores the
  // same durable form — without it the next session inherits only the typed
  // pair and a package-shaped PAR silently loses its package.
  par_normalization,
  min_order_qty,
  min_order_unit,
} = {}) {
  if (!sessionId || !inventoryItemId) {
    return { ok: false, reason: 'missing-args' };
  }
  const parValue = coerceNumericField(par_level);
  const moqValue = coerceNumericField(min_order_qty);

  let templateId = null;
  let storeId = null;
  let countGroupId = null;
  try {
    const { data: session, error: sessionErr } = await supabase
      .from('count_sessions')
      .select('id, template_id, store_id, count_group_id')
      .eq('id', sessionId)
      .maybeSingle();
    if (sessionErr) {
      console.warn('[propagateGlobalCountFieldsToParents] session lookup failed:', sessionErr);
      return { ok: false, reason: 'session-lookup' };
    }
    if (!session) {
      return { ok: false, reason: 'session-not-found' };
    }
    templateId = session.template_id ?? null;
    storeId = session.store_id ?? null;
    countGroupId = session.count_group_id ?? null;
  } catch (err) {
    console.warn('[propagateGlobalCountFieldsToParents] session lookup threw:', err);
    return { ok: false, reason: 'session-lookup' };
  }

  // Persist a re-resolved template_id back onto count_sessions so future edits
  // skip the resolution work. Best-effort — failures are logged but never thrown.
  const persistResolvedTemplateId = async (resolvedId, label) => {
    try {
      await supabase
        .from('count_sessions')
        .update({ template_id: resolvedId })
        .eq('id', sessionId);
      console.log(
        `[propagateGlobalCountFieldsToParents] Re-resolved template_id=${resolvedId} ` +
        `for session ${sessionId} via ${label}.`
      );
    } catch (_) {}
  };

  // Strongest signal for re-resolution: a sibling session in the same
  // count_group already has a template_id. Stack templates and order-mode
  // sessions are grouped this way, so a single sibling lookup is more
  // accurate than the inventory-overlap heuristic below.
  if (!templateId && countGroupId) {
    try {
      const { data: groupSiblings } = await supabase
        .from('count_sessions')
        .select('template_id')
        .eq('count_group_id', countGroupId)
        .not('template_id', 'is', null)
        .limit(20);
      const siblingTemplateIds = [...new Set(
        (groupSiblings || []).map(r => r.template_id).filter(Boolean)
      )];
      if (siblingTemplateIds.length === 1) {
        templateId = siblingTemplateIds[0];
        await persistResolvedTemplateId(templateId, `count_group_id=${countGroupId} sibling`);
      } else if (siblingTemplateIds.length > 1) {
        // Multiple siblings disagree — pick the most recent (largest id) and persist.
        siblingTemplateIds.sort((a, b) => Number(b) - Number(a));
        templateId = siblingTemplateIds[0];
        await persistResolvedTemplateId(
          templateId,
          `count_group_id=${countGroupId} (newest of ${siblingTemplateIds.length} sibling templates)`
        );
      }
    } catch (err) {
      console.warn('[propagateGlobalCountFieldsToParents] count_group_id resolution failed:', err);
    }
  }

  // If count_group_id resolution didn't find a template, fall back to the
  // inventory-overlap heuristic. Look at the session's items and pick the
  // count_template_items template that contains the highest unique-item
  // overlap with this session. The previous version of this heuristic
  // demanded ≥ 3 matches, ≥ 50 % ratio, AND a 2× margin over the runner-up.
  // The 2× margin in particular kept rejecting clear winners (e.g. 10 vs 7),
  // and the 3-match floor kept rejecting small order sessions. Both of those
  // are now relaxed:
  //   • The match ratio must exceed MIN_RATIO (still 50 %).
  //   • The best candidate must have strictly MORE matches than the second-best.
  //   • Ties (and the legacy 2× rule) are only enforced when the ratio is
  //     low (< HIGH_RATIO); a clear high-ratio winner wins outright.
  //   • On tied counts we tiebreak to the most recent template (largest id)
  //     instead of bailing.
  // The store_id filter is also lifted on retry: older count_template_items
  // rows may have null store_id, and dropping the filter is safe because we
  // still join through count_items which is scoped to this session.
  if (!templateId) {
    const MIN_RATIO = 0.5;
    const HIGH_RATIO = 0.8;
    try {
      const { data: ciRows } = await supabase
        .from('count_items')
        .select('inventory_item_id')
        .eq('count_session_id', sessionId)
        .not('inventory_item_id', 'is', null)
        .limit(30);
      const itemIds = [...new Set((ciRows || []).map(r => r.inventory_item_id).filter(Boolean))];
      if (itemIds.length > 0) {
        // First try: store-scoped lookup (preferred when cti rows are stamped
        // with store_id). Second try: no store filter, in case cti rows have
        // null store_id. We only do the second query if the first returned
        // nothing, to avoid widening the search when scoped data exists.
        let ctiRows = null;
        if (storeId) {
          const { data } = await supabase
            .from('count_template_items')
            .select('template_id, inventory_item_id')
            .in('inventory_item_id', itemIds)
            .eq('store_id', storeId)
            .not('template_id', 'is', null)
            .limit(400);
          ctiRows = data;
        }
        if (!ctiRows || ctiRows.length === 0) {
          const { data } = await supabase
            .from('count_template_items')
            .select('template_id, inventory_item_id')
            .in('inventory_item_id', itemIds)
            .not('template_id', 'is', null)
            .limit(400);
          ctiRows = data;
        }
        if (ctiRows && ctiRows.length > 0) {
          // Count unique inventory_item_ids per template (not raw rows) so
          // templates with multiple location rows per item don't get an
          // inflated score that could mis-link an ad-hoc session.
          const seenPerTemplate = new Map(); // template_id -> Set<inventory_item_id>
          ctiRows.forEach(r => {
            if (!r.template_id || !r.inventory_item_id) return;
            if (!seenPerTemplate.has(r.template_id)) seenPerTemplate.set(r.template_id, new Set());
            seenPerTemplate.get(r.template_id).add(r.inventory_item_id);
          });
          // Build a list and sort by (matches desc, template_id desc) so a
          // tied count breaks toward the most recently created template
          // instead of bailing.
          const candidates = [...seenPerTemplate.entries()]
            .map(([tid, itemSet]) => ({ tid, count: itemSet.size }))
            .sort((a, b) => {
              if (b.count !== a.count) return b.count - a.count;
              return Number(b.tid) - Number(a.tid);
            });
          const best = candidates[0];
          const second = candidates[1] || { count: 0 };
          const ratio = best.count / itemIds.length;
          // Confident when:
          //   • the winner covers ≥ 50 % of the session's items, AND
          //   • either it strictly beats the runner-up (any margin), OR
          //     ties with very high coverage (≥ 80 %) — in which case the
          //     largest template_id tiebreak above wins deterministically.
          const isConfident = (
            ratio >= MIN_RATIO &&
            (best.count > second.count || ratio >= HIGH_RATIO)
          );
          if (isConfident) {
            templateId = best.tid;
            await persistResolvedTemplateId(
              templateId,
              `inventory overlap (${best.count}/${itemIds.length} items, ratio=${ratio.toFixed(2)}, runner-up=${second.count})`
            );
          } else {
            console.log(
              `[propagateGlobalCountFieldsToParents] Template re-resolution did not meet confidence threshold ` +
              `for session ${sessionId}: bestCount=${best.count}, ratio=${ratio.toFixed(2)}, secondCount=${second.count}. ` +
              'Treating as ad-hoc (no-template-id).'
            );
          }
        }
      }
    } catch (err) {
      console.warn('[propagateGlobalCountFieldsToParents] template re-resolution failed:', err);
    }
  }

  const result = {
    ok: true,
    parPropagated: false,
    inventoryParPropagated: false,
    moqPropagated: false,
  };

  const parProvided = parValue !== undefined;
  const parIsValid = parValue === null || (Number.isFinite(parValue) && parValue >= 0);
  const parDefaulted = par_defaulted === true;

  if (parProvided && parIsValid) {
    const legacyPatch = { par_level: parValue, par_defaulted: parDefaulted };
    if (par_unit) legacyPatch.par_unit = par_unit;
    // The durable form travels with the legacy pair or not at all: a template
    // row holding a fresh par_level beside a stale par_normalized_quantity is
    // the "two PAR truths on one row" failure, one session removed.
    const normalization = parNormalizationPatchFrom(par_normalization);
    const patch = normalization ? { ...legacyPatch, ...normalization } : legacyPatch;

    if (templateId) {
      // Normal path: update count_template_items so the template editor shows
      // the new value on the next open.
      try {
        const { data: updatedRows, error: parErr } = await writeTemplatePar(
          (body) => supabase
            .from('count_template_items')
            .update(body)
            .eq('template_id', templateId)
            .eq('inventory_item_id', inventoryItemId)
            .select('id'),
          patch,
          legacyPatch,
        );

        if (parErr) {
          console.warn('[propagateGlobalCountFieldsToParents] PAR -> count_template_items failed:', parErr);
        } else if (!updatedRows || updatedRows.length === 0) {
          // The UPDATE matched 0 rows. This can happen because either:
          //   (a) the item has no row in count_template_items at all (user
          //       added it to the count after creation), or
          //   (b) a type-mismatch / RLS issue silently prevented the match
          //       even though rows DO exist for (template_id, inventory_item_id).
          //
          // Handle case (b) first: query existing rows by PK and update them
          // directly. This avoids leaving old 0-PAR rows in place while
          // inserting a duplicate new row at a different sort position.
          let existingTemplateRowIds = [];
          try {
            const { data: existingRows } = await supabase
              .from('count_template_items')
              .select('id')
              .eq('template_id', templateId)
              .eq('inventory_item_id', inventoryItemId);
            existingTemplateRowIds = (existingRows || []).map(r => r.id);
          } catch (_) {}

          if (existingTemplateRowIds.length > 0) {
            // Rows exist — update them by PK so the existing template rows
            // (which the template editor shows) actually receive the new PAR.
            try {
              const { error: pkPatchErr } = await writeTemplatePar(
                (body) => supabase
                  .from('count_template_items')
                  .update(body)
                  .in('id', existingTemplateRowIds),
                patch,
                legacyPatch,
              );
              if (!pkPatchErr) {
                result.parPropagated = true;
              } else {
                console.warn(
                  '[propagateGlobalCountFieldsToParents] PAR UPDATE by PK failed:',
                  pkPatchErr
                );
              }
            } catch (e) {
              console.warn('[propagateGlobalCountFieldsToParents] PAR UPDATE by PK threw:', e);
            }
          } else {
          // Neither the direct UPDATE nor the by-PK lookup found existing rows.
          // Before inserting, try to resolve the canonical inventory_item_id
          // from count_items in case the caller passed a count_items.id or a
          // snapshot id that differs from the FK stored in count_template_items.
          // If we find a match this way, update those rows instead of inserting.
          let resolvedByCountItems = false;
          try {
            const { data: ciMatchRows } = await supabase
              .from('count_items')
              .select('inventory_item_id')
              .eq('count_session_id', sessionId)
              .or(`id.eq.${inventoryItemId},inventory_item_id.eq.${inventoryItemId}`)
              .not('inventory_item_id', 'is', null)
              .limit(10);
            const resolvedIds = [...new Set(
              (ciMatchRows || []).map(r => r.inventory_item_id).filter(
                id => id && String(id) !== String(inventoryItemId)
              )
            )];
            for (const resolvedId of resolvedIds) {
              const { data: resolvedTemplateRows } = await supabase
                .from('count_template_items')
                .select('id')
                .eq('template_id', templateId)
                .eq('inventory_item_id', resolvedId);
              if (resolvedTemplateRows && resolvedTemplateRows.length > 0) {
                const { error: resolvedPatchErr } = await writeTemplatePar(
                  (body) => supabase
                    .from('count_template_items')
                    .update(body)
                    .in('id', resolvedTemplateRows.map(r => r.id)),
                  patch,
                  legacyPatch,
                );
                if (!resolvedPatchErr) {
                  result.parPropagated = true;
                  resolvedByCountItems = true;
                  console.log(
                    `[propagateGlobalCountFieldsToParents] Resolved id-shape mismatch: ` +
                    `original inventoryItemId=${inventoryItemId} -> resolved=${resolvedId}. ` +
                    `Updated ${resolvedTemplateRows.length} template row(s).`
                  );
                }
                break;
              }
            }
          } catch (ciErr) {
            console.warn('[propagateGlobalCountFieldsToParents] count_items id-shape resolution failed:', ciErr);
          }

          if (!resolvedByCountItems) {
          // Case (a): item has no template row at all. Insert one so PAR sticks.
          console.warn(
            '[propagateGlobalCountFieldsToParents] PAR UPDATE matched 0 rows in count_template_items ' +
            `for templateId=${templateId}, inventoryItemId=${inventoryItemId}. ` +
            'Item was likely added to the count after creation. Inserting new template row(s) so PAR sticks.'
          );
          const inserted = await upsertNewTemplateRowsForItem({
            templateId,
            inventoryItemId,
            storeId,
            sessionId,
            parValue,
            par_unit,
            parDefaulted,
            parNormalization: normalization,
          });
          if (inserted) {
            result.parPropagated = true;
            result.parFallback = 'template-row-inserted';
          } else {
            console.warn(
              '[propagateGlobalCountFieldsToParents] template-row insert failed; ' +
              'falling back to inventory_items.par_level.'
            );
            const saved = await fallbackParToInventoryItem(
              inventoryItemId,
              storeId,
              parValue,
              'no-template-row',
              parDefaulted,
            );
            if (saved) {
              result.parPropagated = true;
              result.inventoryParPropagated = true;
              result.parFallback = 'no-template-row';
            }
          }
          } // end if (!resolvedByCountItems)
          } // end else (existingTemplateRowIds.length === 0)
        } else {
          result.parPropagated = true;
        }
      } catch (err) {
        console.warn('[propagateGlobalCountFieldsToParents] PAR propagation threw:', err);
      }
    } else {
      // No parent template (ad-hoc / EOP session). Write PAR to the
      // inventory master so the user's edit is not silently discarded.
      const saved = await fallbackParToInventoryItem(
        inventoryItemId,
        storeId,
        parValue,
        'no-template-id',
        parDefaulted,
      );
      if (saved) {
        result.parPropagated = true;
        result.inventoryParPropagated = true;
        result.parFallback = 'no-template-id';
      }
    }

    // Mirror PAR to inventory_items.par_level on every successful template
    // update too. The template editor reads count_template_items.par_level
    // first and falls back to inventory_items.par_level, so a cti row left
    // with par_level=0 (or null) on a different template would otherwise
    // keep showing a stale inv.par_level. Writing both keeps the two views
    // in sync. The fallback path above already writes inv.par_level, so
    // skip the mirror only when the fallback itself already wrote inventory.
    // A newly inserted template row still needs this mirror or another
    // template will continue to inherit the old master PAR/provenance.
    const inventoryFallbackAlreadySaved = result.parFallback === 'no-template-id'
      || result.parFallback === 'no-template-row';
    if (
      result.parPropagated &&
      !inventoryFallbackAlreadySaved &&
      storeId
    ) {
      try {
        // par_level only — inventory_items has no par_unit column (see
        // fallbackParToInventoryItem).
        const { error: inventoryMirrorError } = await supabase
          .from('inventory_items')
          .update({ par_level: parValue, par_defaulted: parDefaulted })
          .eq('id', inventoryItemId)
          .eq('store_id', storeId);
        if (inventoryMirrorError) {
          console.warn(
            '[propagateGlobalCountFieldsToParents] inventory_items mirror failed:',
            inventoryMirrorError,
          );
        } else {
          result.inventoryParPropagated = true;
        }
      } catch (err) {
        console.warn('[propagateGlobalCountFieldsToParents] inventory_items mirror failed:', err);
      }
    }
  }

  const moqProvided = moqValue !== undefined;
  const moqIsValid = moqValue === null || (Number.isFinite(moqValue) && moqValue >= 0);
  if (moqProvided && moqIsValid && storeId) {
    const patch = { moq: moqValue };
    if (min_order_unit) patch.moq_unit = min_order_unit;
    try {
      const { error: moqErr } = await supabase
        .from('inventory_items')
        .update(patch)
        .eq('id', inventoryItemId)
        .eq('store_id', storeId);
      if (moqErr) {
        console.warn('[propagateGlobalCountFieldsToParents] MOQ -> inventory_items failed:', moqErr);
      } else {
        result.moqPropagated = true;
      }
    } catch (err) {
      console.warn('[propagateGlobalCountFieldsToParents] MOQ propagation threw:', err);
    }
  }

  // --- Template count_session propagation ---
  // For sessions that belong to a recurring group (count_group_id is set),
  // also write the PAR to the sibling is_template=true count_session's
  // count_items so the template editor view (which reads count_items for
  // the template session) reflects the user's latest value immediately.
  //
  // MOQ is deliberately excluded from this mirror. A minimum order quantity
  // belongs to a VENDOR PRODUCT (purchase_options.moq_packages); mirroring one
  // onto count_items is how a single supplier's minimum became an item-level
  // fact that four vendors then disagreed with. The inventory_items.moq write
  // above is retained for the non-counting callers that still edit the legacy
  // item record (CountSessionItemEditor, CounterIntelligenceDrawer), and both
  // of those write their own count_items rows directly, so nothing loses a
  // write it was relying on here.
  if (countGroupId && storeId && parProvided) {
    try {
      // A recurring group normally has exactly one is_template=true session,
      // but anomalous data can produce more than one. Fetch them all (newest
      // first) and mirror to each. .maybeSingle() 400s (PGRST116) on >1 row,
      // which previously aborted this whole mirror and left the toast reading
      // "template could not be updated".
      const { data: tmplSessions, error: tmplErr } = await supabase
        .from('count_sessions')
        .select('id')
        .eq('count_group_id', countGroupId)
        .eq('store_id', storeId)
        .eq('is_template', true)
        .order('id', { ascending: false });

      if (tmplErr) {
        console.warn('[propagateGlobalCountFieldsToParents] template session lookup failed:', tmplErr);
      } else if (Array.isArray(tmplSessions) && tmplSessions.length > 0) {
        // Build the patch for count_items on the template session(s).
        // count_items DOES have a par_unit column, so that is safe here.
        const tmplPatch = {};
        if (parProvided && parIsValid) {
          tmplPatch.par_level = parValue;
          tmplPatch.par_defaulted = parDefaulted;
          if (par_unit) tmplPatch.par_unit = par_unit;
        }

        if (Object.keys(tmplPatch).length > 0) {
          // Snapshot/location/position from the child count's row — describes the
          // source item (not the template session), so fetch once and reuse for
          // any inserts below.
          let itemSnapshot = null;
          let itemLocation = 'Individual Items';
          let itemCountPosition = null;
          try {
            const { data: childRow } = await supabase
              .from('count_items')
              .select('item_snapshot, location, count_position')
              .eq('count_session_id', sessionId)
              .eq('inventory_item_id', inventoryItemId)
              .limit(1)
              .maybeSingle();
            if (childRow) {
              itemSnapshot = childRow.item_snapshot ?? null;
              itemLocation = childRow.location || 'Individual Items';
              itemCountPosition = childRow.count_position ?? null;
            }
          } catch (_) {}

          for (const tmpl of tmplSessions) {
            const tmplSessionId = tmpl.id;
            try {
              // Try to update an existing row in this template session's count_items.
              const { data: updatedTmplRows, error: tmplUpdateErr } = await supabase
                .from('count_items')
                .update(tmplPatch)
                .eq('count_session_id', tmplSessionId)
                .eq('inventory_item_id', inventoryItemId)
                .select('id');

              if (tmplUpdateErr) {
                console.warn('[propagateGlobalCountFieldsToParents] template session count_items update failed:', tmplUpdateErr);
                continue;
              }

              if (updatedTmplRows && updatedTmplRows.length > 0) {
                // Successfully updated existing template count_items row(s).
                console.log(
                  `[propagateGlobalCountFieldsToParents] Updated ${updatedTmplRows.length} count_items row(s) ` +
                  `on template session ${tmplSessionId} for inventoryItemId=${inventoryItemId}.`
                );
                if (parProvided && parIsValid) {
                  result.parPropagated = true;
                  // Normalise any prior fallback — we reached the template
                  // session, so the toast should read as a template update.
                  result.parFallback = 'template-session-updated';
                }
                notifyTemplateSessionItemsChanged({
                  templateSessionId: tmplSessionId,
                  inventoryItemId,
                  action: 'updated',
                });
              } else {
                // No existing count_items row for this item on this template
                // session. Insert one so the item appears in the template editor
                // with the correct PAR.
                console.log(
                  `[propagateGlobalCountFieldsToParents] No count_items row found on template session ` +
                  `${tmplSessionId} for inventoryItemId=${inventoryItemId}. Inserting new row.`
                );
                const newRow = {
                  count_session_id: tmplSessionId,
                  inventory_item_id: inventoryItemId,
                  item_snapshot: itemSnapshot,
                  location: itemLocation,
                  count_position: itemCountPosition,
                  expected_quantity: '',
                  actual_quantity: '',
                  is_counted: false,
                  ...tmplPatch,
                };
                const { error: insertErr } = await supabase
                  .from('count_items')
                  .insert(newRow);
                if (insertErr) {
                  console.warn('[propagateGlobalCountFieldsToParents] template session count_items insert failed:', insertErr);
                } else {
                  console.log(
                    `[propagateGlobalCountFieldsToParents] Inserted new count_items row on template session ` +
                    `${tmplSessionId} for inventoryItemId=${inventoryItemId}.`
                  );
                  if (parProvided && parIsValid) {
                    result.parPropagated = true;
                    result.parFallback = 'template-session-updated';
                  }
                  notifyTemplateSessionItemsChanged({
                    templateSessionId: tmplSessionId,
                    inventoryItemId,
                    action: 'inserted',
                  });
                }
              }
            } catch (perSessionErr) {
              console.warn('[propagateGlobalCountFieldsToParents] template session mirror threw:', perSessionErr);
            }
          }
        }
      }
    } catch (err) {
      console.warn('[propagateGlobalCountFieldsToParents] template session propagation threw:', err);
    }
  }

  return result;
}

/**
 * Build a short, user-facing confirmation message from a propagation result.
 * Returns null when nothing was propagated, so callers can use the falsy
 * value to decide whether to show a toast at all.
 *
 *   { parPropagated: true }                                  -> "PAR updated in template"
 *   { parPropagated: true, parFallback: 'no-template-id' }   -> "This count isn't linked to a template — PAR was saved to the item only"
 *   { parPropagated: true, parFallback: 'template-row-inserted' } -> "PAR saved — added to template as a new row"
 *   { parPropagated: true, parFallback: 'no-template-row' }   -> "PAR saved — could not update template row"
 *   { moqPropagated: true }                                  -> "MOQ updated for this item"
 *   PAR + MOQ together                                       -> two phrases joined with " • "
 *
 * Note: MOQ is always persisted to inventory_items (not the template), so we
 * keep the PAR and MOQ phrases distinct rather than collapsing them into a
 * single "PAR & MOQ updated in template" message that would be inaccurate.
 */
export function formatPropagationToast(result) {
  if (!result || result.ok === false) return null;
  const parOk = !!result.parPropagated;
  const moqOk = !!result.moqPropagated;
  if (!parOk && !moqOk) return null;
  const parts = [];
  if (parOk) {
    if (!result.parFallback || result.parFallback === 'template-session-updated') {
      parts.push('PAR updated in template');
    } else if (result.parFallback === 'no-template-id') {
      parts.push("This count isn\u2019t linked to a template \u2014 PAR was saved to the item only");
    } else if (result.parFallback === 'template-row-inserted') {
      parts.push('PAR saved \u2014 added to template as a new row');
    } else {
      parts.push('PAR saved \u2014 could not update template row');
    }
  }
  if (moqOk) {
    parts.push('MOQ updated for this item');
  }
  return parts.join(' \u2022 ');
}

export default propagateGlobalCountFieldsToParents;
